/**
 * WikiPages/index.tsx
 * desc: near-fullscreen dialog hosting the wiki pages workspace; opened from the nav
 * note: the workspace is lazy-loaded so its editor bundle isn't paid for on every page
 *       load, and a pointerdown on a toast/confirmation must not count as "outside"
 */
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import DialogUntyped from '../../components/Dialog';
import Loader from '../../components/Loader';
import { translate } from '../../utilities/localization';
import './styles.css';

// Dialog is still JS: its destructured props infer as required `any`s, so pin the
// contract this shell relies on until the component converts
interface DialogProps {
    children?: ReactNode;
    classNameContent?: string;
    description?: string;
    onInteractOutside?: (event: Event) => void;
    onOpen: (open: boolean) => void;
    open: boolean;
    title?: string;
}
const Dialog = DialogUntyped as unknown as ComponentType<DialogProps>;

const WikiPagesContent = lazy(() => import('./Content'));

export interface WikiPagesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

// radix treats a pointerdown on a toast or a toastr confirmation as an outside
// interaction — swallow it so confirming a delete doesn't also dismiss the dialog
const handleInteractOutside = (event: Event) => {
    if (event.target instanceof Element && event.target.closest('.redux-toastr')) {
        event.preventDefault();
    }
};

const WikiPagesDialog = ({ open, onOpenChange }: WikiPagesDialogProps) => (
    <Dialog
        classNameContent="wiki-pages-dialog"
        description={ translate('wikiPages.dialog.description') }
        onInteractOutside={ handleInteractOutside }
        onOpen={ onOpenChange }
        open={ open }
        title={ translate('wikiPages.dialog.title') }
    >
        <Suspense fallback={ <Loader/> }>
            <WikiPagesContent/>
        </Suspense>
    </Dialog>
);


export default WikiPagesDialog;
